<template>
  <div
    class="bookmarks"
    role="region"
    aria-label="My Bookmarks"
    data-testid="slide-bookmarks-pane"
  >
    <div ref="scrollEl" class="bookmarks-scroll">
      <div v-if="bookmarkedSlides.length" class="bookmarks-grid">
        <SlideTypeCard
          v-for="slide in bookmarkedSlides"
          :key="slide.id"
          :slide="slide"
          with-quick-add
          :class="{ '--flash': slide.id === flashedId }"
          @select="onSelect"
          @quick-add="onSelect"
        />
      </div>
      <div v-else class="bookmarks-empty">
        <span class="material-symbols-rounded filled bookmarks-empty-icon">bookmark</span>
        <h3 class="bookmarks-empty-title">No bookmarks yet</h3>
        <p class="bookmarks-empty-body">
          Slides you save will appear here for one-click access. Switch to
          the Marketplace to browse and bookmark anything you'll reuse.
        </p>
        <button
          type="button"
          class="bookmarks-empty-cta"
          data-testid="bookmarks-empty-cta"
          @click="$emit('go-marketplace')"
        >
          <span class="material-symbols-rounded">storefront</span>
          <span>Browse Marketplace</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import {
  SLIDE_CATALOG,
  DEFAULT_FAVORITE_IDS,
  type SlideType,
} from '../data/slideTypeCatalog'
import { pinnedStore, isInPicker } from '../lib/pinnedStore'
import SlideTypeCard from './SlideTypeCard.vue'

const POPULARITY_INDEX: Record<string, number> = DEFAULT_FAVORITE_IDS.reduce(
  (acc, id, i) => {
    acc[id] = i
    return acc
  },
  {} as Record<string, number>,
)

export default Vue.extend({
  name: 'SlideBookmarksPane',
  components: { SlideTypeCard },
  computed: {
    bookmarkedSlides(): SlideType[] {
      return SLIDE_CATALOG
        .filter(isInPicker)
        .sort((a, b) => {
          const ai = POPULARITY_INDEX[a.id] ?? Number.POSITIVE_INFINITY
          const bi = POPULARITY_INDEX[b.id] ?? Number.POSITIVE_INFINITY
          return ai - bi
        })
    },
    flashedId(): string | null {
      return pinnedStore.lastPickedId
    },
  },
  watch: {
    flashedId(newId: string | null) {
      if (!newId) return
      this.$nextTick(() => this.scrollFlashedIntoView(newId))
    },
  },
  methods: {
    onSelect(slide: SlideType) {
      this.$emit('select', slide)
    },
    scrollFlashedIntoView(id: string) {
      const scrollEl = this.$refs.scrollEl as HTMLElement | undefined
      const card = scrollEl?.querySelector<HTMLElement>(`[data-slide-id="${id}"]`)
      card?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    },
  },
})
</script>

<style scoped lang="scss">
.bookmarks {
  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
  width: 100%;
  height: 100%;
  background: #FAFAFB;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  color: #1A1A2E;
  min-width: 0;
  container-type: inline-size;
  container-name: bookmarks;
}

.bookmarks-scroll {
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 18px 22px 22px;
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 15, 20, 0.18) transparent;
}
.bookmarks-scroll::-webkit-scrollbar { width: 6px; }
.bookmarks-scroll::-webkit-scrollbar-thumb {
  background: rgba(15, 15, 20, 0.18);
  border-radius: 3px;
}

.bookmarks-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}
@container bookmarks (max-width: 900px) {
  .bookmarks-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@container bookmarks (max-width: 720px) {
  .bookmarks-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@container bookmarks (max-width: 540px) {
  .bookmarks-grid { grid-template-columns: 1fr; }
}

.bookmarks-empty {
  display: grid;
  place-items: center;
  text-align: center;
  gap: 10px;
  padding: 64px 24px;
  max-width: 480px;
  margin: 0 auto;
  color: rgba(15, 15, 20, 0.6);
}
.bookmarks-empty-icon {
  width: 72px;
  height: 72px;
  display: grid;
  place-items: center;
  font-size: 40px;
  color: var(--aha-pink);
  background: rgba(255, 64, 129, 0.10);
  border-radius: 999px;
}
.bookmarks-empty-title {
  font-size: 20px;
  font-weight: 800;
  color: #1A1A2E;
  margin: 0;
}
.bookmarks-empty-body {
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}
.bookmarks-empty-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 10px 18px;
  border: 0;
  background: var(--aha-purple);
  color: white;
  border-radius: 999px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 160ms var(--ease-out), transform 160ms var(--ease-out);
}
.bookmarks-empty-cta:hover {
  background: var(--aha-pink);
  transform: translateY(-1px);
}
.bookmarks-empty-cta .material-symbols-rounded { font-size: 18px; }
</style>
